import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAssaysGroupsModal from '@/modules/configurationModule/detail/assays/configuration-assays-groups/components/configuration-assays-groups-modal/configuration-assays-groups-modal.vue'
import ConfigurationAssaysGroupsModalComp from '@/modules/configurationModule/detail/assays/configuration-assays-groups/components/configuration-assays-groups-modal/configuration-assays-groups-modal'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationAssaysGroupsGridState, initialConfigurationAssaysGroupsGridState } from '@/modules/configurationModule/detail/assays/configuration-assays-groups/components/configuration-assays-groups-grid/configuration-assays-groups-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { ConfigurationAssaygroupResponse, ConfigurationAssaygroupCreateRequest, ConfigurationAssaygroupPatchInBatchRequest } from '@/modules/configurationModule/detail/assays/configuration-assays-groups/services/configuration-assaygroup/configuration-assaygroup.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAssaysGroupsModal
    }
})
export default class ConfigurationAssaysGroupsGrid extends BaseCrudServerSideComponent<ConfigurationAssaygroupResponse> implements BaseCrud {
    @Ref() readonly configurationAssaysGroupsModalWrapper!: typeof ModalWrapper
    @Ref() readonly configurationAssaysGroupsModal!: ConfigurationAssaysGroupsModalComp

    state: ConfigurationAssaysGroupsGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAssaysGroupsGridState()
    }

    /// SERVICES CALLS ///

    private async _createAssaygroup(createRequest: ConfigurationAssaygroupCreateRequest): Promise<ConfigurationAssaygroupResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.configurationAssaysGroupsModal.hide()
            return response
        }
        return null
    }

    private async _patchAssaysgroups(batchRequests: ConfigurationAssaygroupPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                    this.configurationAssaysGroupsModal.hide()
                }
                return response
            }
        }
        return null
    }

    private async _deleteAssaysgroups(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationAssaygroupCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAssaygroup(createRequest)) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async patchOperation(batchRequest: ConfigurationAssaygroupPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAssaysgroups([batchRequest])) {
                this.reloadData()
            }
        }
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAssaysgroups(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(assaygroup: ConfigurationAssaygroupResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (assaygroup) {
            this.showModal(this.$tc('configuration.actions.update'), assaygroup, this.configurationAssaysGroupsModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assaygroup => assaygroup.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.configurationAssaysGroupsModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
